/**
 * CountryChangeVerifyDialog — second-factor dialog when a user tries
 * to change their country inside the 7-day cooldown.
 *
 * Feb 2026 user spec: *"once a week, the next change will next
 * verification and location turned on"* — i.e. the user gets one free
 * country change every 7 days; any additional change inside that
 * window has to prove (a) they know the account password and (b)
 * they're physically in the target country (browser geolocation →
 * reverse-geocoded → ISO country code).
 *
 * Flow inside this dialog
 *   1. Show the requested-country target + a "Turn on Location"
 *      button.
 *   2. Call `navigator.geolocation.getCurrentPosition` → get lat/lon
 *      → reverse-geocode via BigDataCloud's free client-side API
 *      (no key needed; runs in the browser). Result is an ISO-2 code.
 *   3. User types their current password.
 *   4. Click Verify. Parent then PATCHes /api/auth/profile with the
 *      verification_password + verification_geo_country fields.
 */
import React, { useState } from 'react';
import { MapPin, Lock, ShieldCheck, X, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { COUNTRIES } from '../utils/countries';

const CODE_TO_NAME = Object.fromEntries(COUNTRIES.map((c) => [c.code, `${c.flag} ${c.name}`]));

/** Reverse-geocode lat/lon → ISO-2 country code using BigDataCloud's
 *  free (no key, CORS-enabled) endpoint. Falls back to `null` on any
 *  error so the caller can show a graceful "couldn't determine" state. */
const reverseGeocodeToCountry = async (lat, lon) => {
  try {
    const r = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`,
    );
    if (!r.ok) return null;
    const data = await r.json();
    const code = String(data?.countryCode || '').toUpperCase();
    return code || null;
  } catch {
    return null;
  }
};

const CountryChangeVerifyDialog = ({
  open,
  onClose,
  targetCountry,      // ISO-2 code the user wants to switch to
  onVerified,         // (password, geoCountry) => Promise<void>
  serverMessage,      // Message from the 409 that triggered this dialog
}) => {
  const [password, setPassword] = useState('');
  const [geoState, setGeoState] = useState('idle');   // idle | requesting | ok | denied | mismatch | error
  const [geoCountry, setGeoCountry] = useState('');
  const [geoError, setGeoError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const requestLocation = () => {
    if (!('geolocation' in navigator)) {
      setGeoState('error');
      setGeoError('This browser does not expose the Geolocation API.');
      return;
    }
    setGeoState('requesting');
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords || {};
        const code = await reverseGeocodeToCountry(latitude, longitude);
        if (!code) {
          setGeoState('error');
          setGeoError("Couldn't determine country from your location. Try again in a moment.");
          return;
        }
        setGeoCountry(code);
        if (code === (targetCountry || '').toUpperCase()) {
          setGeoState('ok');
        } else {
          setGeoState('mismatch');
        }
      },
      (err) => {
        setGeoState('denied');
        setGeoError(err?.message || 'Location permission denied.');
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 0 },
    );
  };

  const canVerify =
    password.trim().length > 0 && geoState === 'ok' && !submitting;

  const handleVerify = async () => {
    if (!canVerify) return;
    setSubmitting(true);
    try {
      await onVerified(password.trim(), geoCountry);
    } finally {
      setSubmitting(false);
    }
  };

  const targetLabel = CODE_TO_NAME[(targetCountry || '').toUpperCase()] || targetCountry;

  return (
    <div
      className="fixed inset-0 z-[230] flex items-center justify-center px-4 py-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="country-verify-dialog"
    >
      <div className="w-full max-w-md bg-slate-900 border-2 border-indigo-500/40 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-indigo-900/40 to-slate-900">
          <div className="flex items-start gap-3 min-w-0">
            <ShieldCheck className="w-6 h-6 text-indigo-300 mt-0.5" />
            <div className="min-w-0">
              <h2 className="text-white text-base font-black">Verify country change</h2>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Changing to <strong className="text-white">{targetLabel}</strong>. Once a week is free — additional changes need verification.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 -mr-1"
            data-testid="country-verify-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {serverMessage && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-[12px] text-amber-100 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{serverMessage}</span>
            </div>
          )}

          {/* Location step */}
          <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-indigo-300 mt-0.5" />
              <div className="text-sm text-slate-200 flex-1">
                <p className="font-semibold">Step 1 · Turn on Location</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  We compare your device's coordinates to the new country. Nothing is stored beyond the resulting country code.
                </p>
              </div>
            </div>
            {geoState === 'idle' && (
              <Button
                onClick={requestLocation}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                data-testid="country-verify-geo-btn"
              >
                <MapPin className="w-4 h-4 mr-1.5" /> Turn on Location
              </Button>
            )}
            {geoState === 'requesting' && (
              <p className="text-[12px] text-slate-300 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Waiting for location…
              </p>
            )}
            {geoState === 'ok' && (
              <p className="text-[12px] text-emerald-300 font-semibold">
                ✓ Location matches: {CODE_TO_NAME[geoCountry] || geoCountry}
              </p>
            )}
            {geoState === 'mismatch' && (
              <div className="text-[12px] text-rose-300">
                Location resolved to <strong>{CODE_TO_NAME[geoCountry] || geoCountry}</strong> — doesn't match {targetLabel}.
                <button type="button" onClick={requestLocation} className="ml-1 underline hover:text-white">Try again</button>
              </div>
            )}
            {(geoState === 'denied' || geoState === 'error') && (
              <div className="text-[12px] text-rose-300">
                {geoError || 'Location unavailable.'}
                <button type="button" onClick={requestLocation} className="ml-1 underline hover:text-white">Try again</button>
              </div>
            )}
          </div>

          {/* Password step */}
          <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Lock className="w-4 h-4 text-amber-300 mt-0.5" />
              <div className="text-sm text-slate-200 flex-1">
                <p className="font-semibold">Step 2 · Confirm your password</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Same password you use to log in.
                </p>
              </div>
            </div>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="country-verify-password"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-800 bg-slate-950/60 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Cancel</Button>
          <Button
            onClick={handleVerify}
            disabled={!canVerify}
            className="bg-indigo-500 hover:bg-indigo-400 text-white font-bold disabled:opacity-40"
            data-testid="country-verify-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & change country'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CountryChangeVerifyDialog;
